import React, { FC, useEffect, useState, useRef, Fragment } from 'react'
import ReactMarkdown from 'react-markdown'
import Team from './parts/About/Team'
import {
  Grid,
  NavColumn,
  NavLink,
  BodyContainer,
  Section,
  Desktop,
  Markdown,
  Video,
} from './parts/About'
import { Title } from '../../shared_components/Title'
import { Box } from '../../shared_components/UI/Box'
import { IPool, Investor } from '../../types/reducerTypes'
import styled from 'styled-components'
import { wrapEthOrUsdb } from '../Home/useUpcomingProjects'
import { AngelCard } from '../Home/OurBackers'
import VestingSchedule from './parts/About/VestingSchedule'
import { useWindowSize } from '@uidotdev/usehooks'
import { useHistory } from 'react-router-dom'
import { useKycStatus } from './Checkout/KYC'
import { useSelector } from 'react-redux'
import { selectUser } from '../../modules/user/selectors'
import useWeb3 from '../../hooks/useWeb3'
import { Tooltip } from 'react-tooltip'

const About: FC<{ pool: IPool }> = ({ pool }) => {
  const {
    about,
    team,
    media,
    poolSettings: { requiresKYC },
    slug,
  } = pool

  const gridRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLDivElement>(null)

  const { isVetted: isLabsPool } = pool ?? {}

  const [activeIndex, setActiveIndex] = useState<number>(0)

  const shouldDisplayKeyDetails = slug !== 'worldofblast'

  const sections = [
    {
      title: 'Key Details',
      ref: useRef<HTMLDivElement>(null),
      content: [{ type: 'key_details', data: '' }],
      shouldDisplay: shouldDisplayKeyDetails,
    },
    {
      title: 'About',
      ref: useRef<HTMLDivElement>(null),
      content: [{ type: 'text', data: about?.highlightsText }],
      shouldDisplay: about?.highlightsText !== null,
    },
    {
      title: 'Product',
      ref: useRef<HTMLDivElement>(null),
      content: [{ type: 'text', data: about?.productText }],
      shouldDisplay: about?.productText !== null && isLabsPool,
    },
    {
      title: 'Token utility',
      ref: useRef<HTMLDivElement>(null),
      content: [{ type: 'text', data: about?.tokenUtility }],
      shouldDisplay: about?.tokenUtility !== null && isLabsPool,
    },
    {
      title: 'Investors',
      ref: useRef<HTMLDivElement>(null),
      content: [{ type: 'investors', data: about?.investors }],
      shouldDisplay: about?.investors?.length > 0,
    },
    {
      title: 'Token Vesting',
      ref: useRef<HTMLDivElement>(null),
      content: [{ type: 'token_vesting', data: about?.vestingSchedule }],
      shouldDisplay: about?.vestingSchedule?.length > 0,
    },
    {
      title: 'Team',
      ref: useRef<HTMLDivElement>(null),
      content: [{ type: 'team', data: team?.entries }],
      shouldDisplay: team?.entries?.length > 0,
    },
  ]

  const filteredSections = sections.filter(
    ({ shouldDisplay, content }) =>
      shouldDisplay &&
      (!!content?.[0]?.data?.length || content?.[0]?.type === 'key_details'),
  )

  const scrollToSection = (index: number) => {
    const el = filteredSections?.[index]?.ref?.current
    const elTop = el?.getBoundingClientRect()?.top

    if (elTop) {
      window.scroll({
        top: elTop + document.documentElement.scrollTop - 120,
        behavior: 'smooth',
      })
    }
    setActiveIndex(index)
  }

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      if (gridRef?.current && scrollY) {
        const { top: gridPos } = gridRef.current.getBoundingClientRect()
        if (gridPos < 0) {
          if (navRef?.current) {
            if (
              parseFloat(navRef.current.style.top.split('px')[0]) + 120 >
                -gridPos - 32 ||
              parseFloat(navRef.current.style.top.split('px')[0]) + 120 <
                -gridPos + 32
            ) {
              navRef.current.style.top = `${(-gridPos + 120).toString()}px`
            }
          }

          for (let i = 0; i < filteredSections.length; i++) {
            const section = filteredSections[i]
            const rect = section?.ref?.current?.getBoundingClientRect()
            if (rect?.top && scrollY) {
              if (rect.top > -90) {
                setActiveIndex(i)
                break
              }
            }
            const isLast = i === sections.length - 1
            if (isLast) {
              setActiveIndex(i)
            }
          }
        } else {
          if (navRef?.current) navRef.current.style.top = `80px`
          setActiveIndex(0)
        }
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredSections])

  const isTbc = pool?.isTbc
  const isEthRaise = pool?.poolSettings?.raiseCurrency === 'eth'
  const softCap = Number(pool?.poolSettings?.softCap ?? 0)

  const formattedSoftCap = isTbc
    ? ''
    : `of ${wrapEthOrUsdb(softCap, isEthRaise)}`
  const formattedEndTime = isTbc
    ? 'the end of the fundraise'
    : createLongformDatetimeString(new Date(pool?.privateSale?.end))

  const { width: screenWidth } = useWindowSize()
  const isMobile = screenWidth < 1000

  return about ? (
    <Grid ref={gridRef}>
      {isLabsPool && (
        <Desktop>
          <NavColumn ref={navRef} id="nav-column">
            {filteredSections.map(({ title }, i) => (
              <NavLink
                key={`nav-link-${i}`}
                isActive={activeIndex === i}
                onClick={() => scrollToSection(i)}
                isLast={i === filteredSections.length - 1}
              >
                {title}
              </NavLink>
            ))}
          </NavColumn>
        </Desktop>
      )}
      <BodyContainer hasSidebar={isLabsPool}>
        <Box dc g={32}>
          {filteredSections.map(({ title, ref, content }, i) => (
            <React.Fragment key={`section-${i}-${title}`}>
              <Section
                ref={ref}
                key={`about-section-${new Date().getTime() * Math.random()}`}
                id={`${title}`}
              >
                <SectionTitle>{title}</SectionTitle>
                {content.map(({ type, data }, i: number) => {
                  return type === 'key_details' ? (
                    <>
                      <KeyDetails
                        key={`${i}-detials`}
                        softCap={formattedSoftCap}
                        endTime={formattedEndTime}
                        isKyc={requiresKYC}
                      />
                      {media?.highlightsVideo?.url && (
                        <Box flex={1}>
                          <Video
                            controls={isMobile}
                            autoPlay
                            muted
                            playsInline
                            loop
                            key={`data-video-${
                              new Date().getTime() * Math.random()
                            }`}
                          >
                            <source
                              src={media?.highlightsVideo?.url}
                              type="video/mp4"
                            />
                          </Video>
                        </Box>
                      )}
                    </>
                  ) : type === 'investors' ? (
                    <Investors
                      key={`data-investors-${
                        new Date().getTime() * Math.random()
                      }`}
                      investors={data}
                    />
                  ) : type === 'token_vesting' ? (
                    <VestingSchedule
                      key={`data-schedule-${
                        new Date().getTime() * Math.random()
                      }`}
                      isEthRaise={isEthRaise}
                      vestingSchedule={data}
                    />
                  ) : type === 'text' ? (
                    data ? (
                      <Markdown
                        key={`data-about-${
                          new Date().getTime() * Math.random()
                        }`}
                      >
                        <ReactMarkdown>{data}</ReactMarkdown>
                      </Markdown>
                    ) : (
                      <React.Fragment
                        key={`${i}-data-about-${
                          new Date().getTime() * Math.random()
                        }`}
                      ></React.Fragment>
                    )
                  ) : type === 'team' ? (
                    <Team
                      key={`data-about-${new Date().getTime() * Math.random()}`}
                      team={data}
                    />
                  ) : (
                    <Video
                      controls
                      key={`data-video-${new Date().getTime() * Math.random()}`}
                    >
                      <source src={data} type="video/mp4" />
                    </Video>
                  )
                })}
              </Section>
            </React.Fragment>
          ))}
        </Box>
      </BodyContainer>
    </Grid>
  ) : (
    <></>
  )
}

export default About

function Investors({ investors }: { investors: Investor[] }) {
  return (
    <InvestorContainer g={32} fw>
      {investors.map(({ imageUrl, href, name }, index) => {
        return (
          <a
            href={href}
            target="_blank"
            rel="noreferrer noopeneer"
            key={`investor-${index}`}
          >
            <AngelCard dc className="vc-card" c g={20}>
              <img src={imageUrl} alt={name} />
              <span>{name}</span>
            </AngelCard>
          </a>
        )
      })}
    </InvestorContainer>
  )
}

function KeyDetails({
  softCap,
  endTime,
  isKyc = false,
}: {
  softCap: string
  endTime: string
  isKyc?: boolean
}) {
  const history = useHistory()
  const user = useSelector(selectUser)
  const { initWeb3 } = useWeb3()
  const isLoggedIn = !!user?.walletAddress
  const { kycComplete } = useKycStatus()
  const DETAILS = [
    {
      title: 'All or nothing',
      showShieldCheck: true,
      copy: `This project will only be funded if it reaches its goal ${softCap} by ${endTime}.`,
    },
    {
      title: 'KYC',
      shouldHide: !isKyc,
      copy: kycComplete
        ? 'You have already completed KYC so are now able to take part in this raise.'
        : 'You will need to complete KYC in order to take part in this raise.',
      cta: kycComplete
        ? undefined
        : {
            text: 'Complete KYC',
            onClick: () => {
              if (kycComplete) return
              if (isLoggedIn) {
                history.push('/account#kyc')
                return
              }
              initWeb3()
            },
          },
    },
    {
      title: 'Overallocation Pool',
      copy: 'This raise runs an overallocation logic. If there is more demand than tokens available, your investment is diluted.',
      cta: {
        text: 'Learn more',
        tooltip: `Example: a project has a fundraise goal of $1m. If the project receives $2m in funding, each participant will receive 50% the tokens they wanted to purchase along with a refund of 50% of their initial purchase.`,
      },
    },
  ]

  return (
    <KeyDetailsContainer g={32} fw>
      {DETAILS.map(({ title, showShieldCheck, shouldHide, copy, cta }, i) => {
        if (shouldHide) return <Fragment key={i} />
        return (
          <DetailBox key={i}>
            <Box dc asb>
              <Box dc g={8} fu>
                <Box g={8} fw ac>
                  {showShieldCheck && (
                    <img src="/img/icons/shield-check.svg" alt="shield check" />
                  )}
                  <h4>{title}</h4>
                </Box>
                <p>{copy}</p>
              </Box>
              <Box ac g={4}>
                {cta && (
                  <Box data-tooltip-id={'cta-tooltip-' + i} fu aw>
                    <Box ac g={8} fu className="cta">
                      <span className="psuedo-link" onClick={cta.onClick}>
                        {cta.text}
                      </span>
                      {cta.tooltip ? (
                        <></>
                      ) : (
                        <img
                          className="chevron"
                          src="/img/icons/chevron-right.svg"
                          alt="chevron right"
                        />
                      )}
                    </Box>
                  </Box>
                )}
                {!!cta?.tooltip && (
                  <>
                    <Tooltip
                      id={'cta-tooltip-' + i}
                      opacity={1}
                      style={{ maxWidth: '230px', zIndex: 1 }}
                      delayShow={200}
                      delayHide={200}
                    >
                      <span>{cta.tooltip}</span>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>
          </DetailBox>
        )
      })}
    </KeyDetailsContainer>
  )
}

const KeyDetailsContainer = styled(Box)`
  padding-top: 2rem;
  padding-bottom: 3rem;
`

const SectionTitle = styled(Title)`
  font-variant-numeric: ordinal;
  font-feature-settings: 'dlig' on, 'ss01' on, 'dnom' on;
  text-transform: none;
  font-size: 48px;
  font-style: normal;
  font-weight: 700;
  line-height: 54px; /* 112.5% */
  letter-spacing: -0.96px;
`

const DetailBox = styled(Box)`
  padding: 20px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(20px);
  min-height: 174px;
  min-width: 312px;
  max-width: 312px;
  h4 {
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.4px;
  }
  p {
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    line-height: 21px; /* 150% */
    letter-spacing: -0.14px;
  }
  span.psuedo-link {
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
  }
  img.chevron {
    margin-top: 1px;
  }
  .cta {
    :hover {
      filter: brightness(0.8);
      cursor: pointer;
    }
  }
  .narrow-tooltip {
    max-width: 300px;
    background: ${({ theme }) => theme.semantics.zap.surface.primary};
    opacity: 1;
  }
  @media (max-width: 1000px) {
    width: 100%;
    max-width: 100%;
  }
`

const InvestorContainer = styled(Box)`
  padding-top: 1.5rem;
  a,
  img,
  ${AngelCard} {
    width: 226px;
  }
  img {
    height: 196px;
    border-radius: 4px;
  }
  @media (max-width: 1000px) {
    a,
    img,
    ${AngelCard} {
      width: 100%;
      max-width: calc(100vw - 32px);
    }
    img {
      height: auto;
      border-radius: 4px;
    }
  }
`

function createLongformDatetimeString(date) {
  // Extract day of the week
  const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' })

  // Extract full month name and day
  const monthAndDay = date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
  })

  // Extract year
  const year = date.getFullYear()

  // Extract time in 12-hour format with AM/PM and include seconds
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  // Add the UTC suffix
  const utcSuffix = 'UTC'

  // Combine all parts together
  const longformDatetime = `${dayOfWeek}, ${monthAndDay} ${year} ${time} ${utcSuffix}`

  return longformDatetime
}
